"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.httpGatewayReadChunkAsArrayBuffer = exports.httpGatewayReadAsArrayBuffer = exports.base64Encoding = exports.getMessageFromError = exports.getSdkTransfer = exports.httpGatewaySelectFileFolderDialog = exports.handleHttpGatewayDrop = exports.createHtmlInputElement = exports.httpGetTransfer = exports.httpGetAllTransfers = exports.httpRemoveTransfer = exports.httpStopTransfer = exports.setupHttpGateway = exports.initHttpGateway = exports.sendTransferUpdate = void 0;
var messages_1 = require("../constants/messages");
var helpers_1 = require("../helpers/helpers");
var index_1 = require("../index");
var v2_1 = require("./v2");
// Maximum file size for generating image previews for files. 50MiB matches the limits in both Connect and IBM Aspera for desktop.
var MAX_FILE_SIZE = 50 * 1024 * 1024;
/**
 * HTTP Gateway Core Logic
 * - File/Folder picking
 * - Starting and testing
 *
 * @remarks
 * Most logic is called directly by Desktop SDK functions
 * You may not need to import anything from this file.
 */
/**
 * Send a transfer update through the SDK
 *
 * @param transfer - Transsfer object to send to consumers
 */
var sendTransferUpdate = function (transfer) {
    index_1.asperaSdk.httpGatewayTransferStore.set(transfer.uuid, transfer);
    index_1.asperaSdk.activityTracking.handleTransferActivity({
        type: 'transferUpdated',
        data: { transfers: [transfer] },
    });
};
exports.sendTransferUpdate = sendTransferUpdate;
/**
 * Initialize the HTTP Gateway after the /info response has been received and verified.
 * For v2 gateways, delegates to the old HTTP Gateway SDK.
 * For v3 gateways, sets up the iframe container for downloads.
 *
 * @param response - The /info response from the HTTP Gateway server
 *
 * @returns a promise that resolves when the HTTP Gateway is initialized
 */
var initHttpGateway = function (response) {
    index_1.asperaSdk.globals.httpGatewayInfo = response;
    index_1.asperaSdk.globals.httpGatewayVerified = true;
    var majorVersion = Number(response.version.split('.')[0] || 3);
    index_1.asperaSdk.globals.httpGatewayRoutePrefix = majorVersion >= 3 ? '/v3' : '';
    if (index_1.asperaSdk.useOldHttpGateway) {
        // Watch for old HTTP Gateway transfers in case used.
        (0, v2_1.registerActivityCallback)(function (oldHttpTransfers) {
            oldHttpTransfers.transfers.forEach(function (oldHttpTransfer) {
                var transfer = oldHttpTransfer;
                transfer.transfer_client = 'http-gateway';
                (0, exports.sendTransferUpdate)(transfer);
            });
        });
        return (0, v2_1.initHttpGateway)(index_1.asperaSdk.globals.httpGatewayUrl).then(function () { });
    }
    var iframeContainer = document.createElement('div');
    iframeContainer.id = 'aspera-http-gateway-iframes';
    iframeContainer.style = 'display: none;';
    document.body.appendChild(iframeContainer);
    index_1.asperaSdk.globals.httpGatewayIframeContainer = iframeContainer;
    return Promise.resolve();
};
exports.initHttpGateway = initHttpGateway;
/**
 * Set up the HTTP Gateway by normalizing the URL, fetching the /info endpoint,
 * and calling initHttpGateway with the response.
 *
 * @param url - The HTTP Gateway URL provided by the consumer
 *
 * @returns a promise that resolves when the HTTP Gateway is initialized
 */
var setupHttpGateway = function (url) {
    var finalUrl = url.trim();
    if (finalUrl.indexOf('http') !== 0) {
        finalUrl = "https://".concat(finalUrl);
    }
    if (finalUrl.endsWith('/')) {
        finalUrl = finalUrl.slice(0, -1);
    }
    index_1.asperaSdk.globals.httpGatewayUrl = finalUrl;
    return fetch("".concat(finalUrl, "/info"), { method: 'GET' }).then(function (response) {
        return response.json().then(function (responseData) {
            if (response.status >= 400) {
                throw Error(responseData);
            }
            return responseData;
        });
    }).then(function (response) {
        return (0, exports.initHttpGateway)(response);
    });
};
exports.setupHttpGateway = setupHttpGateway;
/**
 * Stop an in-progress HTTP Gateway transfer.
 * Aborts the underlying HTTP request and sets the transfer status to 'cancelled'.
 *
 * Note: If the download is being directly handled by the browser's download manager, this will return
 * an error. The user must cancel the download themselves in the browser's download manger.
 *
 * @param id - ID of the transfer
 *
 * @returns Promise indicating success
 */
var httpStopTransfer = function (id) {
    if (index_1.asperaSdk.useOldHttpGateway) {
        (0, v2_1.cancelTransfer)(id);
        return Promise.resolve();
    }
    var transfer = index_1.asperaSdk.httpGatewayTransferStore.get(id);
    if (!transfer) {
        return Promise.reject((0, helpers_1.generateErrorBody)(messages_1.messages.stopTransferFailed, { reason: 'Not found' }));
    }
    else if (transfer.httpDownloadExternalHandle) {
        return Promise.reject((0, helpers_1.generateErrorBody)(messages_1.messages.stopTransferFailedExternal, { reason: 'External handle' }));
    }
    transfer.status = 'cancelled';
    var request = index_1.asperaSdk.httpGatewayRequestStore.get(id);
    if (request) {
        request.abort();
        index_1.asperaSdk.httpGatewayRequestStore.delete(id);
    }
    (0, exports.sendTransferUpdate)(transfer);
    return Promise.resolve();
};
exports.httpStopTransfer = httpStopTransfer;
/**
 * Remove a transfer from HTTP Gateway systems
 * @param id - ID of the transfer
 *
 * @returns Promise indicating success
 */
var httpRemoveTransfer = function (id) {
    if (index_1.asperaSdk.useOldHttpGateway) {
        (0, v2_1.removeTransfer)(id);
        return Promise.resolve({ removed: true });
    }
    /* Cancel the transfer before removing it.
     *
     * Note: This is slightly different from the behavior in the v2 JS SDK. When removing
     * a transfer, v2 will NOT cancel it beforehand. In the desktop and Connect transfer clients,
     * removing a transfer also stops it, so we do the same here for HTTP Gateway v3.
     */
    return (0, exports.httpStopTransfer)(id)
        .catch(function () { })
        .then(function () {
        index_1.asperaSdk.httpGatewayTransferStore.delete(id);
        return { removed: true };
    });
};
exports.httpRemoveTransfer = httpRemoveTransfer;
/**
 * Get the list of http gateway transfers
 *
 * @returns list of HTTP Gateway
 */
var httpGetAllTransfers = function () {
    if (index_1.asperaSdk.useOldHttpGateway) {
        return (0, v2_1.getAllTransfers)().transfers;
    }
    return Array.from(index_1.asperaSdk.httpGatewayTransferStore.values());
};
exports.httpGetAllTransfers = httpGetAllTransfers;
/**
 * Get a HTTP Gateway transfer by ID
 *
 * @returns a transfer or null
 */
var httpGetTransfer = function (id) {
    if (index_1.asperaSdk.useOldHttpGateway) {
        return (0, v2_1.getTransferById)(id);
    }
    return index_1.asperaSdk.httpGatewayTransferStore.get(id);
};
exports.httpGetTransfer = httpGetTransfer;
/**
 * Create HTML input element for file picking
 */
var createHtmlInputElement = function () {
    var element = window.document.createElement('input');
    element.type = 'file';
    element.style = 'display: none;';
    window.document.body.appendChild(element);
    return element;
};
exports.createHtmlInputElement = createHtmlInputElement;
/**
 * Handle drop events and store files for HTTP Gateway
 * This works on top of desktop.
 */
var handleHttpGatewayDrop = function (items, callback, event) {
    var files = [];
    var callbackCount = 0;
    var callbackFinishCount = 0;
    var finalCallback = function () {
        if (callbackFinishCount >= callbackCount) {
            var finalFiles = files.map(function (file) {
                index_1.asperaSdk.httpGatewaySelectedFiles.set(file.name, file);
                return {
                    lastModified: file.lastModified,
                    name: file.name,
                    size: file.size,
                    type: file.type
                };
            });
            callback({ event: event, files: { dataTransfer: { files: finalFiles } } });
        }
    };
    var traverse = function (item) {
        if (item.isFile) {
            item.file(function (file) {
                files.push(file);
                callbackFinishCount++;
                finalCallback();
            });
        }
        else if (item.isDirectory) {
            var dirReader = item.createReader();
            dirReader.readEntries(function (entries) {
                for (var k = 0; k < entries.length; k++) {
                    callbackCount++;
                    traverse(entries[k]);
                }
            });
            callbackFinishCount++;
        }
        else {
            callbackFinishCount++;
            finalCallback();
        }
    };
    for (var i = 0; i < items.length; i++) {
        var item = items[i].webkitGetAsEntry();
        if (item) {
            callbackCount++;
            traverse(item);
        }
    }
};
exports.handleHttpGatewayDrop = handleHttpGatewayDrop;
/**
 * Open native browser file or folder picker for files
 *
 * @param options - File picker options
 * @param folder - Indicate if choosing folders
 *
 * @returns Promise that resolves with info about the files picked
 */
var httpGatewaySelectFileFolderDialog = function (options, folder) {
    if (index_1.asperaSdk.useOldHttpGateway) {
        return (folder ? (0, v2_1.getFoldersForUploadPromise)((options === null || options === void 0 ? void 0 : options.http_gateway_v2_transfer_id) || '') : (0, v2_1.getFilesForUploadPromise)((options === null || options === void 0 ? void 0 : options.http_gateway_v2_transfer_id) || ''));
    }
    var _a = (0, helpers_1.generatePromiseObjects)(), promise = _a.promise, rejecter = _a.rejecter, resolver = _a.resolver;
    var element = (0, exports.createHtmlInputElement)();
    element.multiple = !!(options === null || options === void 0 ? void 0 : options.multiple);
    if (folder) {
        element.webkitdirectory = true;
    }
    element.oncancel = function () {
        rejecter({ debugData: { code: -32002, message: messages_1.messages.filePickerCancel } });
    };
    element.onchange = function () {
        var returnFiles = [];
        for (var i = 0; i < element.files.length; i++) {
            var file = element.files[i];
            returnFiles.push(file);
            index_1.asperaSdk.httpGatewaySelectedFiles.set(file.webkitRelativePath || file.name, file);
        }
        resolver({
            dataTransfer: {
                files: returnFiles.map(function (file) {
                    return {
                        size: file.size,
                        lastModified: file.lastModified,
                        name: file.webkitRelativePath || file.name,
                        type: file.type,
                    };
                })
            }
        });
    };
    element.click();
    return promise;
};
exports.httpGatewaySelectFileFolderDialog = httpGatewaySelectFileFolderDialog;
/**
 * Generate a human-readable title for an HTTP Gateway transfer. The desktop and Connect
 * transfer clients populate `title` themselves; HTTP Gateway transfers are constructed
 * client-side and need a synthesized title for consumer UIs.
 *
 * If the caller already supplied a `title` on the spec, that wins. Otherwise we use the
 * basename of the first source path, suffixed with `(+N)` when there are additional paths.
 */
var getTitle = function (transferSpec) {
    var _a, _b;
    if (transferSpec.title) {
        return transferSpec.title;
    }
    var paths = transferSpec.paths;
    if (!paths || paths.length === 0) {
        return '';
    }
    var path = (_a = paths[0]) === null || _a === void 0 ? void 0 : _a.source;
    if (!path) {
        return '';
    }
    var first = (_b = path.split('/').filter(Boolean).pop()) !== null && _b !== void 0 ? _b : '';
    if (paths.length === 1) {
        return first;
    }
    return "".concat(first, " (+").concat(paths.length - 1, ")");
};
/**
 * Get a generic transfer object for HTTP Gateway transfers.
 *
 * @param transferSpec - TransferSpec being provided for the HTTP Gateway transfer
 *
 * @returns a transfer object to track status and send to consumers
 */
var getSdkTransfer = function (transferSpec) {
    var title = getTitle(transferSpec);
    return {
        uuid: (0, helpers_1.randomUUID)(),
        transfer_spec: transferSpec,
        current_file: '',
        add_time: new Date().toISOString(),
        file_counts: {
            attempted: 0,
            completed: 0,
            failed: 0,
            skipped: 0,
        },
        end_time: '',
        explorer_path: '',
        previous_status: 'initiating',
        status: 'initiating',
        bytes_written: 0,
        bytes_expected: 0,
        calculated_rate_kbps: 0,
        elapsed_usec: 0,
        percentage: 0,
        title: title,
        remaining_usec: 0,
        transfer_client: 'http-gateway',
        httpGatewayTransfer: true,
        httpDownloadExternalHandle: false,
    };
};
exports.getSdkTransfer = getSdkTransfer;
/**
 * Try to parse and get a useful string from API calls for HTTP Gateway
 *
 * @param error - Error from API call for Gateway
 *
 * @returns a string to use for errors
 */
var getMessageFromError = function (error) {
    var data = (0, helpers_1.safeJsonParse)(error);
    var message = messages_1.messages.httpNetworkFail;
    var code = 500;
    if (data && typeof data === 'object') {
        message = data.message || data.description || messages_1.messages.httpNetworkFail;
        code = data.code || 500;
    }
    else if (error && typeof error === 'object') {
        message = error.message || error.description || messages_1.messages.httpNetworkFail;
        code = error.code || 500;
    }
    else if (typeof error === 'string') {
        message = error;
    }
    return {
        message: message,
        code: code,
    };
};
exports.getMessageFromError = getMessageFromError;
/**
 * Encodes a JSON string as a base64 string.
 *
 * The returned value uses the standard base64 alphabet and must not be assumed to be
 * URL safe. It may contain `+`, `/`, or `=` padding characters.
 *
 * This function first converts the input string to UTF-8 bytes using {@link TextEncoder}, then
 * converts those bytes into a binary string before passing it to {@link Window.btoa}.
 *
 * This extra conversion is necessary because `btoa()` only works for strings in which each
 * character occupies only one byte. So if you were to pass a string into `btoa()` containing
 * characters that occupy more than one byte, you will get an error. This is problematic because
 * a transfer spec could potentially have any number of Unicode characters.
 *
 * See the MDN `window.btoa()` documentation for more details:
 * https://developer.mozilla.org/en-US/docs/Web/API/Window/btoa#unicode_strings
 *
 * @param jsonString - The JSON string to encode. May contain arbitrary Unicode characters.
 * @returns A Base64-encoded representation of the UTF-8 bytes of `jsonString`.
 */
var base64Encoding = function (jsonString) {
    var bytes = new TextEncoder().encode(jsonString);
    var binString = Array.from(bytes, function (byte) {
        return String.fromCodePoint(byte);
    }).join('');
    return btoa(binString);
};
exports.base64Encoding = base64Encoding;
/**
 * Attempt to read the specified `File` object contents as a base64-encoded string.
 *
 * @param file - File previously selected by the user
 *
 * @returns an object with base64 data and mime type
 */
var readFileAsBase64 = function (file) {
    return new Promise(function (resolve, reject) {
        var reader = new FileReader();
        reader.onload = function (event) {
            var _a;
            try {
                var dataUrl = (_a = event.target) === null || _a === void 0 ? void 0 : _a.result;
                if (typeof dataUrl !== 'string') {
                    reject((0, helpers_1.generateErrorBody)('FileReader did not return a data URL'));
                    return;
                }
                // Remove `data:*/*;base64,` from the start of the data URL (https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL)
                var base64 = dataUrl.split(';base64,')[1];
                if (base64 === undefined) {
                    reject((0, helpers_1.generateErrorBody)('Invalid data URL format'));
                    return;
                }
                resolve({
                    data: base64,
                    type: file.type || 'application/octet-stream'
                });
            }
            catch (err) {
                reject((0, helpers_1.generateErrorBody)('Failed to process file data', err));
            }
        };
        reader.onerror = function () {
            reject((0, helpers_1.generateErrorBody)('Failed to read file', reader.error));
        };
        reader.readAsDataURL(file);
    });
};
/**
 * Returns the specified file's contents as a base64-encoded string.
 *
 * Note: The maximum file size allowed is 50 MiB.
 *
 * @param path path to the file to read
 *
 * @returns a promise that resolves with the file data as a base64-encoded string and mime type
 */
var httpGatewayReadAsArrayBuffer = function (path) {
    // Pre-condition: Caller must have previously selected file via `showSelectFileDialog()`
    var file = index_1.asperaSdk.httpGatewaySelectedFiles.get(path);
    if (!file) {
        return (0, helpers_1.throwError)(messages_1.messages.fileNotAllowed);
    }
    if (file.size > MAX_FILE_SIZE) {
        return (0, helpers_1.throwError)('File exceeds allowed maximum');
    }
    return readFileAsBase64(file);
};
exports.httpGatewayReadAsArrayBuffer = httpGatewayReadAsArrayBuffer;
/**
 * Attempt to read the specified `File` object contents as a base64-encoded string.
 *
 * @param file - File previously selected by the user
 * @param offset offset to start reading the file, in bytes
 * @param chunkSize the size of the chunk to read, in bytes
 *
 * @returns an object with base64 data and mime type
 */
var readFileChunkAsBase64 = function (file, offset, chunkSize) {
    return new Promise(function (resolve, reject) {
        if (offset > file.size) {
            resolve({ data: '', type: file.type || 'application/octet-stream' });
            return;
        }
        var chunk = file.slice(offset, offset + chunkSize);
        var reader = new FileReader();
        reader.onload = function (event) {
            var _a;
            try {
                var dataUrl = (_a = event.target) === null || _a === void 0 ? void 0 : _a.result;
                if (typeof dataUrl !== 'string') {
                    reject((0, helpers_1.generateErrorBody)('FileReader did not return a data URL'));
                    return;
                }
                // Remove `data:*/*;base64,` from the start of the data URL (https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL)
                var base64 = dataUrl.split(';base64,')[1];
                if (base64 === undefined) {
                    reject((0, helpers_1.generateErrorBody)('Invalid data URL format'));
                    return;
                }
                resolve({
                    data: base64,
                    type: file.type || 'application/octet-stream'
                });
            }
            catch (err) {
                reject((0, helpers_1.generateErrorBody)('Failed to process file data', err));
            }
        };
        reader.onerror = function () {
            reject((0, helpers_1.generateErrorBody)('Failed to read file', reader.error));
        };
        reader.readAsDataURL(chunk);
    });
};
/**
 * Read a chunk of a file as a base64-encoded string.
 *
 * Note: The maximum chunk size allowed is 50 MiB.
 *
 * @param path path to the file to read. For pure JS this will just be the filename.
 * @param offset offset to start reading the file, in bytes
 * @param chunkSize the size of the chunk to read, in bytes
 *
 * @returns a promise that resolves with the file chunk data as a base64-encoded string and mime type
 */
var httpGatewayReadChunkAsArrayBuffer = function (path, offset, chunkSize) {
    if (offset < 0 || chunkSize <= 0) {
        return (0, helpers_1.throwError)('Invalid offset or chunk size');
    }
    // Pre-condition: Caller must have previously selected file via `showSelectFileDialog()`
    var file = index_1.asperaSdk.httpGatewaySelectedFiles.get(path);
    if (!file) {
        return (0, helpers_1.throwError)(messages_1.messages.fileNotAllowed);
    }
    if (chunkSize > MAX_FILE_SIZE) {
        return (0, helpers_1.throwError)('Chunk size exceeds allowed maximum');
    }
    return readFileChunkAsBase64(file, offset, chunkSize);
};
exports.httpGatewayReadChunkAsArrayBuffer = httpGatewayReadChunkAsArrayBuffer;
